import * as THREE from 'three';
import type { World } from '../sim/world';
import type { Vessel } from '../sim/vessel';
import { hostile, intentToward, type Intent } from '../sim/captain';
import { TRAIN_LIMIT } from '../sim/cannons';
import { AMMO } from '../data/classes';
import type { Pipeline } from './pipeline';

// Where an enemy's broadsides can reach, painted faintly on the sea: for
// each side of hers with guns loaded, the water her guns can be trained on
// (as long as her line of guns, opening by their traverse, out to what her
// shot carries). It brightens as she brings that side round onto us and
// glows red, pulsing outward, once its guns are run out and laid on us - the
// moment to bear away or put the helm over. Drawn over the finished picture
// like the aim (render/aim.ts), on the water only.

/** Broadsides drawn at once (the most threatening). */
const WEDGES = 6;

const FRAG = /* glsl */ `
uniform sampler2D uDepth;
uniform sampler2D uOpaqueDepth;
uniform mat4 uInvViewProj;
uniform vec2 uScreen;
uniform float uTime;
uniform float uOpacity;
uniform int uCount;
// Origin (her centre) and outward axis (her beam, that side), world xz.
uniform vec4 uA[${WEDGES}];
// Forward (along her length) xz; half the length of her line of guns; the tangent of their traverse.
uniform vec4 uB[${WEDGES}];
// Reach, m; strength 0..1; how hot (0 coming, 1 laid on us); spare.
uniform vec4 uC[${WEDGES}];
in vec2 vUv;

const vec3 AMBER = vec3(0.92, 0.66, 0.36);
const vec3 RED = vec3(0.96, 0.36, 0.26);

void main() {
  vec2 uv = gl_FragCoord.xy / uScreen;
  float d = texture(uDepth, uv).r;
  float od = texture(uOpaqueDepth, uv).r;
  // Only the sea.
  if (d >= 1.0 || d >= od - 1e-7) discard;
  vec4 wp = uInvViewProj * vec4(uv * 2.0 - 1.0, d * 2.0 - 1.0, 1.0);
  vec2 q = wp.xz / wp.w;
  vec3 col = vec3(0.0);
  float alpha = 0.0;
  for (int i = 0; i < ${WEDGES}; i++) {
    if (i >= uCount) break;
    vec2 o = uA[i].xy, axis = uA[i].zw, fwd = uB[i].xy;
    float halfLen = uB[i].z, tanHalf = uB[i].w;
    float reach = uC[i].x, strength = uC[i].y, hot = uC[i].z;
    vec2 rel = q - o;
    float u = dot(rel, axis);
    if (u < 0.0 || u > reach * 1.08) continue;
    float w = dot(rel, fwd);
    // Inside the fan: positive, metres from its side edges.
    float edge = halfLen + u * tanHalf - abs(w);
    float r = length(rel);
    float soft = max(fwidth(edge), 0.6);
    float inside = smoothstep(-soft, soft, edge) * (1.0 - smoothstep(reach * 0.92, reach * 1.04, r));
    if (inside <= 0.0) continue;
    // Strongest near her, fading out toward the end of her reach; kept off her own side.
    float fade = smoothstep(4.0, 28.0, u) * mix(1.0, 0.2, smoothstep(0.3 * reach, reach, r));
    // Her guns laid on us: bands running outward along the line of fire.
    float pulse = hot * (0.5 + 0.5 * sin(u * 0.09 - uTime * 7.0));
    float fill = (0.11 + 0.08 * hot + 0.13 * pulse) * fade;
    // The fan's edges and the end of her reach, as fine lines (gone where a
    // pixel spans more sea than a line would show).
    float fe = fwidth(edge), fr = fwidth(r);
    float line = (1.0 - smoothstep(fe * 1.2, fe * 2.4, abs(edge))) * (1.0 - smoothstep(6.0, 30.0, fe));
    float arc = (1.0 - smoothstep(fr * 1.2, fr * 2.4, abs(r - reach))) * (1.0 - smoothstep(6.0, 30.0, fr)) * step(0.0, edge);
    float a = clamp((fill + 0.75 * line * fade + 0.5 * arc) * inside * strength, 0.0, 1.0);
    vec3 c = mix(AMBER, RED, hot);
    col = col * (1.0 - a) + c * a;
    alpha = alpha + a * (1.0 - alpha);
  }
  if (alpha <= 0.002) discard;
  gl_FragColor = vec4(col, alpha) * uOpacity;
}
`;

interface Lit {
  vessel: Vessel;
  side: 1 | -1;
  /** Shown now (eased toward `want`), and how hot. */
  shown: number;
  want: number;
  hot: number;
  hotWant: number;
}

export class ThreatView {
  private readonly mesh: THREE.Mesh;
  private readonly mat: THREE.ShaderMaterial;
  private readonly a: THREE.Vector4[] = [];
  private readonly b: THREE.Vector4[] = [];
  private readonly c: THREE.Vector4[] = [];
  private readonly lit = new Map<string, Lit>();
  private time = 0;
  /** Off in the settings (or nothing to show). */
  enabled = true;
  /** What each enemy near enough is about with her guns, toward us (the HUD's words for it read this). */
  readonly intents = new Map<number, Intent>();

  constructor(private readonly pipeline: Pipeline) {
    for (let i = 0; i < WEDGES; i++) {
      this.a.push(new THREE.Vector4());
      this.b.push(new THREE.Vector4());
      this.c.push(new THREE.Vector4());
    }
    this.mat = new THREE.ShaderMaterial({
      vertexShader: /* glsl */ `out vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
      fragmentShader: FRAG,
      uniforms: {
        uDepth: { value: null },
        uOpaqueDepth: { value: null },
        uInvViewProj: { value: new THREE.Matrix4() },
        uScreen: { value: new THREE.Vector2(1, 1) },
        uTime: { value: 0 },
        uOpacity: { value: 1 },
        uCount: { value: 0 },
        uA: { value: this.a },
        uB: { value: this.b },
        uC: { value: this.c },
      },
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
    });
    const tri = new THREE.BufferGeometry();
    tri.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
    tri.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 2, 0, 0, 2], 2));
    this.mesh = new THREE.Mesh(tri, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    // Under the aim.
    this.mesh.renderOrder = -1;
    pipeline.overlay.add(this.mesh);
  }

  update(world: World, camera: THREE.PerspectiveCamera, dt: number, night = 0): void {
    this.time += dt;
    const me = world.player;
    this.intents.clear();
    // Who's out there with guns that could be turned on us, and what each is about.
    for (const v of world.vessels) {
      if (v === me || !v.fighting || !v.captain || !(hostile(v, me) || hostile(me, v))) continue;
      const d = Math.hypot(v.ship.com.x - me.ship.com.x, v.ship.com.z - me.ship.com.z);
      if (d > 2600 || !world.lookout.get(v.id)?.visible) continue;
      const it = intentToward(v, me);
      this.intents.set(v.id, it);
      for (const side of [1, -1] as const) {
        const key = `${v.id}:${side}`;
        const l = this.lit.get(key) ?? { vessel: v, side, shown: 0, want: 0, hot: 0, hotWant: 0 };
        this.lit.set(key, l);
        const loaded = v.battery.ready(side) / Math.max(1, v.battery.count(side));
        const facing = side === it.side;
        let want = 0, hot = 0;
        if (it.state !== 'idle' && (facing || it.presenting === side)) {
          if (it.state === 'bearing' && facing) (want = 1), (hot = 1);
          else if (it.state === 'coming') want = 0.65;
          else if (it.state === 'reloading' && facing) want = 0.18 + 0.3 * it.reload;
          else want = 0.3;
        } else if (v.battle && loaded > 0.5 && d < 1600) {
          // In the fight but not (yet) at us: just the outline of it.
          want = 0.22;
        }
        if (loaded < 0.05 && !(it.state === 'reloading' && facing)) want = 0;
        l.want = want;
        l.hotWant = hot;
      }
    }
    // Ease each one in and out; drop those gone.
    const k = 1 - Math.exp(-dt * 5);
    for (const [key, l] of this.lit) {
      if (!this.intents.has(l.vessel.id)) l.want = l.hotWant = 0;
      l.shown += (l.want - l.shown) * k;
      l.hot += (l.hotWant - l.hot) * (1 - Math.exp(-dt * 8));
      if (l.want === 0 && l.shown < 0.01) this.lit.delete(key);
    }
    const show = this.enabled && me.fighting ? [...this.lit.values()].filter((l) => l.shown > 0.01 && l.vessel.alive).sort((x, y) => y.shown + y.hot - (x.shown + x.hot)).slice(0, WEDGES) : [];
    this.mesh.visible = show.length > 0;
    if (!show.length) return;

    camera.updateMatrixWorld();
    const u = this.mat.uniforms;
    (u.uScreen!.value as THREE.Vector2).set(this.pipeline.screenWidth, this.pipeline.screenHeight);
    u.uDepth!.value = this.pipeline.sceneDepth;
    u.uOpaqueDepth!.value = this.pipeline.opaqueDepth;
    (u.uInvViewProj!.value as THREE.Matrix4).multiplyMatrices(camera.matrixWorld, camera.projectionMatrixInverse);
    u.uTime!.value = this.time;
    u.uOpacity!.value = 1 - 0.3 * night;
    u.uCount!.value = show.length;
    const tan = Math.tan(TRAIN_LIMIT);
    show.forEach((l, i) => {
      const s = l.vessel.ship, h = s.heading;
      const fx = Math.sin(h), fz = -Math.cos(h);
      // Her starboard beam is (-fz, fx); port the other way.
      this.a[i]!.set(s.com.x, s.com.z, -fz * l.side, fx * l.side);
      this.b[i]!.set(fx, fz, 15 * s.sx, tan);
      // Out to what her shot carries, but no further than a little past us
      // (the water beyond is no business of ours).
      const us = Math.hypot(me.ship.com.x - s.com.x, me.ship.com.z - s.com.z);
      const reach = Math.min(1400, AMMO[l.vessel.battery.ammo].range, us * 1.25 + 90);
      this.c[i]!.set(reach, Math.min(1, l.shown), l.hot, 0);
    });
  }
}
